import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link } from "react-router-dom";
import { useColors, useResponsive } from "@inbody/core";
import { useAuth } from "@inbody/keycloak";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme, Divider, Button, SwipeableDrawer } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LogoImage } from "../../logo-image/index.js";
import { isMenuItemVisible } from "../../side-menu/utils/isMenuItemVisible.js";
import { MENU_COLLAPSED_WIDTH, MENU_WIDTH, HEADER_HEIGHT } from "../styles/sideMenuStyle.js";
import { SideMenuItem } from "./SideMenuItem.js";
import { useMenuStore } from "../../side-menu/store/SideMenuStore.js";
function LayoutSideMenuV2({
  menuItemsList,
  adminMenuItemsList = [],
  appTitle,
  appSubtitle,
  headerLayout = "vertical",
  onCollapsedChange,
  mobileOpen = false,
  onMobileClose,
  topSlot,
  footerSlot
}) {
  var _a, _b, _c;
  const { t } = useTranslation("ui");
  const { user, logout, checkRoles } = useAuth();
  const { base, gray, brand } = useColors();
  useTheme();
  const { isMobile, isIOS } = useResponsive();
  const location = useLocation();
  const isMenuOpen = useMenuStore((state) => state.isOpen);
  const toggleMenuOpen = useMenuStore((state) => state.toggleMenuOpen);
  const collapsed = !isMenuOpen;
  useEffect(() => {
    if (isMobile && mobileOpen) {
      onMobileClose == null ? void 0 : onMobileClose();
    }
  }, [location.pathname]);
  useEffect(() => {
    onCollapsedChange == null ? void 0 : onCollapsedChange(collapsed);
  }, [collapsed, onCollapsedChange]);
  const [transitioning, setTransitioning] = useState(false);
  const transitionTimerRef = useRef();
  const handleToggleSidebar = () => {
    setTransitioning(true);
    clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = setTimeout(() => setTransitioning(false), 300);
    toggleMenuOpen();
  };
  const handleLogout = () => {
    logout();
  };
  const isCollapsed = isMobile ? false : collapsed;
  const hasVisibleAdminMenu = adminMenuItemsList.some(
    (item) => isMenuItemVisible(item, checkRoles)
  );
  const MenuListComponent = useMemo(() => {
    return /* @__PURE__ */ jsxs(
      List,
      {
        disablePadding: true,
        sx: {
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            width: 0,
            height: 0,
            display: "none"
          }
        },
        children: [
          menuItemsList.map((menuItem) => /* @__PURE__ */ jsx(
            SideMenuItem,
            {
              ...menuItem,
              collapsed: isCollapsed,
              transitioning
            },
            menuItem.id
          )),
          hasVisibleAdminMenu && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Divider, { sx: { my: 0.5 } }),
            !isCollapsed && /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "caption",
                sx: {
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                  color: gray[400],
                  fontWeight: 600,
                  fontSize: "11px",
                  letterSpacing: "0.5px",
                  display: "block"
                },
                children: t("관리자")
              }
            ),
            adminMenuItemsList.map((menuItem) => /* @__PURE__ */ jsx(
              SideMenuItem,
              {
                ...menuItem,
                collapsed: isCollapsed,
                transitioning,
                compact: true
              },
              menuItem.id
            ))
          ] })
        ]
      }
    );
  }, [
    menuItemsList,
    adminMenuItemsList,
    hasVisibleAdminMenu,
    isCollapsed,
    transitioning,
    gray,
    t
  ]);
  const defaultFooter = /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        padding: "16px 20px",
        borderTop: `1px solid ${gray[200]}`,
        backgroundColor: "background.paper"
      },
      children: /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", children: [
        /* @__PURE__ */ jsx(
          Avatar,
          {
            sx: {
              width: 40,
              height: 40,
              background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[400]} 100%)`,
              fontSize: "14px",
              fontWeight: 600
            },
            children: ((_a = user == null ? void 0 : user.firstName) == null ? void 0 : _a[0]) || "U"
          }
        ),
        /* @__PURE__ */ jsxs(Box, { sx: { flex: 1 }, children: [
          /* @__PURE__ */ jsx(
            Typography,
            {
              variant: "body2",
              component: "div",
              sx: { fontWeight: 600, color: gray[800] },
              children: (user == null ? void 0 : user.firstName) || ""
            }
          ),
          /* @__PURE__ */ jsxs(Typography, { variant: "small", component: "div", sx: { color: gray[500] }, children: [
            (_b = user == null ? void 0 : user.attributes) == null ? void 0 : _b.jobTitle,
            " · ",
            (_c = user == null ? void 0 : user.attributes) == null ? void 0 : _c.jobPosition
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          IconButton,
          {
            onClick: handleLogout,
            sx: {
              color: gray[400],
              padding: 1,
              borderRadius: 1.5,
              "&:hover": {
                color: brand[500],
                backgroundColor: brand[25]
              },
              fontSize: "16px"
            },
            children: /* @__PURE__ */ jsx(LogoutIcon, { fontSize: "small" })
          }
        )
      ] })
    }
  );
  const sideMenuContent = /* @__PURE__ */ jsxs(
    Box,
    {
      component: "aside",
      sx: {
        width: MENU_WIDTH,
        backgroundColor: "background.paper",
        borderRight: isMobile ? "none" : `1px solid ${gray[200]}`,
        display: "flex",
        flexDirection: "column",
        ...!isMobile && {
          position: "fixed",
          height: "100vh",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            width: 0,
            height: 0,
            display: "none"
          },
          zIndex: 100,
          transition: "width 0.3s ease",
          left: 0,
          top: 0,
          width: isCollapsed ? MENU_COLLAPSED_WIDTH : MENU_WIDTH
        },
        ...isMobile && {
          height: "100%"
        }
      },
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              borderBottom: `1px solid ${gray[200]}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: isCollapsed ? HEADER_HEIGHT : HEADER_HEIGHT * 2,
              boxSizing: "content-box",
              flexShrink: 0,
              px: isCollapsed ? 0 : 2.5
            },
            children: headerLayout === "horizontal" ? (
              /* ── horizontal: [로고 appTitle] ··· [햄버거] ── */
              /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs(
                  Stack,
                  {
                    direction: "row",
                    alignItems: "center",
                    justifyContent: isCollapsed ? "center" : "space-between",
                    children: [
                      !isCollapsed && /* @__PURE__ */ jsxs(Stack, { direction: "row", alignItems: "center", gap: 0.5, children: [
                        /* @__PURE__ */ jsx(
                          Button,
                          {
                            id: "logo",
                            component: Link,
                            to: "/",
                            "aria-label": "logo",
                            disableRipple: true,
                            sx: {
                              "&:hover": { backgroundColor: "transparent" },
                              px: 0.5,
                              minWidth: "auto",
                              borderRadius: 0
                            },
                            children: /* @__PURE__ */ jsx(LogoImage, {})
                          }
                        ),
                        headerLayout == "horizontal" && /* @__PURE__ */ jsx(
                          Box,
                          {
                            sx: {
                              width: "1px",
                              height: "20px",
                              backgroundColor: gray[200]
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          Typography,
                          {
                            variant: "body1",
                            sx: {
                              fontWeight: 500,
                              color: gray[800]
                            },
                            children: appTitle
                          }
                        )
                      ] }),
                      isMobile ? /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          onClick: onMobileClose,
                          "aria-label": "close menu",
                          sx: {
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            border: `1px solid ${gray[100]}`,
                            backgroundColor: "background.paper",
                            color: gray[500],
                            "&:hover": {
                              backgroundColor: gray[100],
                              color: gray[800],
                              borderColor: gray[100]
                            },
                            fontSize: "14px"
                          },
                          children: /* @__PURE__ */ jsx(CloseIcon, { fontSize: "small" })
                        }
                      ) : /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          onClick: handleToggleSidebar,
                          sx: {
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            border: `1px solid ${gray[100]}`,
                            backgroundColor: "background.paper",
                            color: gray[500],
                            "&:hover": {
                              backgroundColor: gray[100],
                              color: gray[800],
                              borderColor: gray[100]
                            },
                            fontSize: "14px"
                          },
                          children: /* @__PURE__ */ jsx(MenuIcon, { fontSize: "small" })
                        }
                      )
                    ]
                  }
                ),
                !isCollapsed && appSubtitle && /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: "small",
                    sx: {
                      px: 0.5,
                      color: gray[400],
                      fontWeight: 400,
                      display: "block"
                    },
                    children: appSubtitle
                  }
                )
              ] })
            ) : (
              /* ── vertical: [로고 ··· 햄버거] / [appTitle] / [appSubtitle] ── */
              /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs(
                  Stack,
                  {
                    direction: "row",
                    alignItems: "center",
                    justifyContent: isCollapsed ? "center" : "space-between",
                    children: [
                      !isCollapsed && /* @__PURE__ */ jsx(
                        Button,
                        {
                          id: "logo",
                          component: Link,
                          to: "/",
                          "aria-label": "logo",
                          disableRipple: true,
                          sx: {
                            "&:hover": { backgroundColor: "transparent" },
                            px: 0.5,
                            minWidth: "auto"
                          },
                          children: /* @__PURE__ */ jsx(LogoImage, {})
                        }
                      ),
                      isMobile ? /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          onClick: onMobileClose,
                          "aria-label": "close menu",
                          sx: {
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            border: `1px solid ${gray[100]}`,
                            backgroundColor: "background.paper",
                            color: gray[500],
                            "&:hover": {
                              backgroundColor: gray[100],
                              color: gray[800],
                              borderColor: gray[100]
                            },
                            fontSize: "14px"
                          },
                          children: /* @__PURE__ */ jsx(CloseIcon, { fontSize: "small" })
                        }
                      ) : /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          onClick: handleToggleSidebar,
                          sx: {
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            border: `1px solid ${gray[100]}`,
                            backgroundColor: "background.paper",
                            color: gray[500],
                            "&:hover": {
                              backgroundColor: gray[100],
                              color: gray[800],
                              borderColor: gray[100]
                            },
                            fontSize: "14px"
                          },
                          children: /* @__PURE__ */ jsx(MenuIcon, { fontSize: "small" })
                        }
                      )
                    ]
                  }
                ),
                !isCollapsed && /* @__PURE__ */ jsxs(Box, { sx: { px: 0.5 }, children: [
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "body2",
                      sx: { fontWeight: 500, color: gray[800], lineHeight: 1.4 },
                      children: appTitle
                    }
                  ),
                  appSubtitle && /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "small",
                      sx: {
                        color: gray[400],
                        fontWeight: 400,
                        display: "block",
                        mt: 0.5
                      },
                      children: appSubtitle
                    }
                  )
                ] })
              ] })
            )
          }
        ),
        !isCollapsed && topSlot,
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            },
            children: MenuListComponent
          }
        ),
        !isCollapsed && (footerSlot ?? defaultFooter)
      ]
    }
  );
  if (isMobile) {
    return /* @__PURE__ */ jsx(
      SwipeableDrawer,
      {
        anchor: "left",
        open: mobileOpen,
        onClose: () => onMobileClose == null ? void 0 : onMobileClose(),
        onOpen: () => {
        },
        disableDiscovery: isIOS,
        sx: {
          "& .MuiDrawer-paper": {
            width: MENU_WIDTH,
            boxSizing: "border-box"
          }
        },
        children: sideMenuContent
      }
    );
  }
  return sideMenuContent;
}
export {
  LayoutSideMenuV2
};
